/**
 * Opus bitrate options for clinical audio recording.
 *
 * Opus uses a dedicated speech mode (SILK) at lower bitrates and a general
 * audio mode (CELT) at higher bitrates. For single-speaker clinical dictation,
 * speech mode is ideal — it's specifically tuned for voice intelligibility.
 *
 * All values are in bits per second.
 */
export const OPUS_BITRATE_OPTIONS = {
  /**
   * Narrowband speech — phone call quality.
   * Opus SILK mode with limited bandwidth. Noticeable compression artifacts,
   * reduced consonant clarity. Risk of losing uncommon medical terminology
   * (drug names, procedures) in noisy environments. Use only to measure the
   * floor of STT provider tolerance.
   *
   * ~54 KB/min | ~810 KB per 15-min appointment
   */
  SPEECH_NARROWBAND: 6_000,

  /**
   * Wideband speech — VoIP quality.
   * What most internet phone systems use. Good intelligibility for common
   * speech but minor artifacts on sibilants and fricatives. Acceptable for
   * STT testing but may degrade on specialized medical vocabulary in
   * challenging acoustic environments.
   *
   * ~108 KB/min | ~1.6 MB per 15-min appointment
   */
  SPEECH_WIDEBAND: 12_000,

  /**
   * Very good speech — clean and natural.
   * Safe floor for production clinical audio. Opus SILK mode at its best.
   * Clear intelligibility across medical terminology. Slight artifacts only
   * audible in direct A/B comparison with higher bitrates.
   *
   * ~144 KB/min | ~2.2 MB per 15-min appointment
   */
  SPEECH_VERY_GOOD: 16_000,

  /**
   * Excellent speech — virtually transparent.
   * Recommended for production. Indistinguishable from uncompressed for
   * speech content. Handles background noise, multiple speakers, and
   * medical terminology with full fidelity. 62.5% smaller than current
   * default (64 kbps).
   *
   * ~216 KB/min | ~3.2 MB per 15-min appointment
   */
  SPEECH_EXCELLENT: 24_000,

  /**
   * Near-transparent — diminishing returns for speech.
   * Conservative choice for production. Marginal quality improvement over
   * 24 kbps for speech-only content, but provides extra headroom for
   * noisy clinical environments (beeping monitors, HVAC, multiple voices).
   *
   * ~288 KB/min | ~4.3 MB per 15-min appointment
   */
  SPEECH_NEAR_TRANSPARENT: 32_000,

  /**
   * Music quality — current default.
   * Overkill for speech-only clinical recording. Opus transitions to CELT
   * (general audio) mode at this range, optimizing for music fidelity
   * rather than speech. No measurable STT benefit over 32 kbps.
   *
   * ~480 KB/min | ~7.2 MB per 15-min appointment
   */
  MUSIC_QUALITY: 64_000,
} as const;

export type OpusBitrateOption =
  (typeof OPUS_BITRATE_OPTIONS)[keyof typeof OPUS_BITRATE_OPTIONS];

/**
 * Default bitrate for production recordings.
 * Currently set to MUSIC_QUALITY (64 kbps) to maintain backwards compatibility.
 * Target: reduce to SPEECH_EXCELLENT (24 kbps) or SPEECH_NEAR_TRANSPARENT (32 kbps)
 * after A/B testing confirms no STT regression.
 */
export const DEFAULT_OPUS_BITRATE = OPUS_BITRATE_OPTIONS.MUSIC_QUALITY;
